import React, { useEffect, useState } from 'react';
import {
  Box, Flex, HStack, VStack, Heading, Text, Spinner, Tabs, TabList, Tab, TabPanels, TabPanel, Button, FormControl, FormLabel, Input, Textarea, SimpleGrid, Select, Checkbox, useToast, Alert, AlertIcon, Badge, Link, Divider,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaExternalLinkAlt, FaSignOutAlt } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import ServiceZipPicker from './ServiceZipPicker';
import CredentialsUpload from './CredentialsUpload';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

export default function PhlebSettings() {
  const { id: phlebId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [phleb, setPhleb] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: err } = await supabase
          .from('phlebotomist_profiles')
          .select('*')
          .eq('id', phlebId)
          .maybeSingle();
        if (err) throw err;
        if (!cancelled) setPhleb(data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [phlebId]);

  if (loading) return <Flex h="60vh" align="center" justify="center"><Spinner /></Flex>;
  if (error || !phleb) {
    return (
      <Box maxW="md" mx="auto" mt={10}>
        <Alert status="error"><AlertIcon />{error || 'Phlebotomist not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box maxW="1000px" mx="auto" p={{ base: 3, md: 6 }}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
        <HStack>
          <Button size="sm" variant="ghost" leftIcon={<FaArrowLeft />} onClick={() => navigate(`/phlebotomist/${phlebId}`)}>
            Back to draws
          </Button>
        </HStack>
        <Heading size="lg">Settings</Heading>
        <Box />
      </Flex>

      <Tabs variant="enclosed" colorScheme="blue">
        <TabList overflowX="auto" overflowY="hidden" sx={{ '&::-webkit-scrollbar': { display: 'none' } }}>
          <Tab>Profile</Tab>
          <Tab>Service Areas</Tab>
          <Tab>Working Hours</Tab>
          <Tab>Payment</Tab>
          <Tab>Credentials</Tab>
          <Tab>Documents</Tab>
          <Tab>Security</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}><ProfileSection phleb={phleb} onSaved={setPhleb} toast={toast} /></TabPanel>
          <TabPanel px={0}><ServiceAreasSection phleb={phleb} onSaved={setPhleb} toast={toast} /></TabPanel>
          <TabPanel px={0}><WorkingHoursSection phlebId={phlebId} toast={toast} /></TabPanel>
          <TabPanel px={0}><PaymentSection phleb={phleb} toast={toast} /></TabPanel>
          <TabPanel px={0}><CredentialsSection phleb={phleb} onSaved={setPhleb} toast={toast} /></TabPanel>
          <TabPanel px={0}><DocumentsSection phleb={phleb} /></TabPanel>
          <TabPanel px={0}><SecuritySection phleb={phleb} toast={toast} navigate={navigate} /></TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}

// ── Profile ─────────────────────────────────────────────────────────────────
function ProfileSection({ phleb, onSaved, toast }) {
  const [form, setForm] = useState({
    full_name:        phleb.full_name || '',
    email:            phleb.email || '',
    phone:            phleb.phone || '',
    company_name:     phleb.company_name || '',
    company_address:  phleb.company_address || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const { data, error } = await supabase
      .from('phlebotomist_profiles')
      .update(form)
      .eq('id', phleb.id)
      .select()
      .maybeSingle();
    setSaving(false);
    if (error) {
      toast({ title: 'Could not save', description: error.message, status: 'error' });
      return;
    }
    if (data) onSaved(data);
    toast({ title: 'Profile saved', status: 'success', duration: 2500 });
  };

  return (
    <VStack align="stretch" spacing={4} maxW="700px">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <Field label="Full name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
        <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" />
        <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <Field label="Company name" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} />
      </SimpleGrid>
      <FormControl>
        <FormLabel fontSize="sm">Company address</FormLabel>
        <Textarea rows={2} value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} />
      </FormControl>
      <HStack justify="flex-end">
        <Button colorScheme="blue" onClick={save} isLoading={saving}>Save changes</Button>
      </HStack>
    </VStack>
  );
}

// ── Service Areas ───────────────────────────────────────────────────────────
function ServiceAreasSection({ phleb, onSaved, toast }) {
  const [zips, setZips] = useState(Array.isArray(phleb.service_zips) ? phleb.service_zips : []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const { data, error } = await supabase
      .from('phlebotomist_profiles')
      .update({ service_zips: zips })
      .eq('id', phleb.id)
      .select()
      .maybeSingle();
    setSaving(false);
    if (error) { toast({ title: 'Could not save', description: error.message, status: 'error' }); return; }
    if (data) onSaved(data);
    toast({ title: `Saved — ${zips.length} ZIP code${zips.length !== 1 ? 's' : ''}`, status: 'success', duration: 2500 });
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Text fontSize="sm" color="gray.600">
        Pick every NYC ZIP code where you can do draws. The Draws page will filter to these ZIPs by default.
      </Text>
      <ServiceZipPicker value={zips} onChange={setZips} />
      <HStack justify="flex-end">
        <Button colorScheme="blue" onClick={save} isLoading={saving}>Save service areas</Button>
      </HStack>
    </VStack>
  );
}

// ── Working Hours ───────────────────────────────────────────────────────────
function WorkingHoursSection({ phlebId, toast }) {
  const [hours, setHours] = useState(
    DAYS.map((_, i) => ({ day_of_week: i, available: i >= 1 && i <= 5, start_time: '09:00', end_time: '17:00' })),
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('working_hours')
        .select('day_of_week, start_time, end_time')
        .eq('phlebotomist_id', phlebId);
      if (cancelled) return;
      if (Array.isArray(data) && data.length > 0) {
        const byDay = new Map(data.map((r) => [r.day_of_week, r]));
        setHours(DAYS.map((_, i) => {
          const r = byDay.get(i);
          return r
            ? { day_of_week: i, available: true, start_time: r.start_time?.slice(0, 5) || '09:00', end_time: r.end_time?.slice(0, 5) || '17:00' }
            : { day_of_week: i, available: false, start_time: '09:00', end_time: '17:00' };
        }));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [phlebId]);

  const save = async () => {
    setSaving(true);
    await supabase.from('working_hours').delete().eq('phlebotomist_id', phlebId);
    const rows = hours
      .filter((h) => h.available)
      .map((h) => ({ phlebotomist_id: phlebId, day_of_week: h.day_of_week, start_time: h.start_time, end_time: h.end_time }));
    if (rows.length > 0) {
      const { error } = await supabase.from('working_hours').insert(rows);
      if (error) { toast({ title: 'Could not save', description: error.message, status: 'error' }); setSaving(false); return; }
    }
    setSaving(false);
    toast({ title: 'Working hours saved', status: 'success', duration: 2500 });
  };

  if (loading) return <Flex justify="center" py={8}><Spinner /></Flex>;

  return (
    <VStack align="stretch" spacing={3} maxW="700px">
      <Text fontSize="sm" color="gray.600">Uncheck days you don't work. Times are local.</Text>
      {hours.map((h, i) => (
        <HStack key={h.day_of_week} spacing={4} align="center">
          <Checkbox
            isChecked={h.available}
            onChange={(e) => {
              const next = [...hours]; next[i] = { ...next[i], available: e.target.checked }; setHours(next);
            }}
            w="120px"
          >
            {DAYS[h.day_of_week]}
          </Checkbox>
          <Input size="sm" type="time" w="130px" isDisabled={!h.available}
            value={h.start_time}
            onChange={(e) => { const next = [...hours]; next[i] = { ...next[i], start_time: e.target.value }; setHours(next); }}
          />
          <Text fontSize="sm" color="gray.500">to</Text>
          <Input size="sm" type="time" w="130px" isDisabled={!h.available}
            value={h.end_time}
            onChange={(e) => { const next = [...hours]; next[i] = { ...next[i], end_time: e.target.value }; setHours(next); }}
          />
        </HStack>
      ))}
      <HStack justify="flex-end" pt={2}>
        <Button colorScheme="blue" onClick={save} isLoading={saving}>Save hours</Button>
      </HStack>
    </VStack>
  );
}

// ── Payment ─────────────────────────────────────────────────────────────────
function PaymentSection({ phleb, toast }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    bank_account_name: '',
    bank_name: '',
    bank_routing_number: '',
    bank_account_number: '',
    bank_account_type: 'checking',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('phlebotomist_payment_info_history')
        .select('*')
        .eq('phlebotomist_id', phleb.id)
        .eq('is_current', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      setInfo(data || null);
      if (data) {
        setForm({
          bank_account_name: data.bank_account_name || '',
          bank_name: data.bank_name || '',
          bank_routing_number: data.bank_routing_number || '',
          bank_account_number: '',
          bank_account_type: data.bank_account_type || 'checking',
        });
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [phleb.id]);

  const save = async () => {
    if (!form.bank_account_number) {
      toast({ title: 'Account number required', status: 'warning' }); return;
    }
    setSaving(true);
    const now = new Date().toISOString();
    const { data: inserted, error } = await supabase
      .from('phlebotomist_payment_info_history')
      .insert({
        phlebotomist_id: phleb.id,
        is_current: true,
        effective_date: new Date().toISOString().split('T')[0],
        bank_account_name: form.bank_account_name,
        bank_name: form.bank_name,
        bank_routing_number: form.bank_routing_number,
        bank_account_number: form.bank_account_number,
        bank_account_type: form.bank_account_type,
        payment_method: 'ACH',
        payment_frequency: info?.payment_frequency || 'biweekly',
        bank_verified: false,
      })
      .select()
      .single();
    if (error) { toast({ title: 'Could not save', description: error.message, status: 'error' }); setSaving(false); return; }

    await supabase.rpc('mark_previous_payment_info_replaced', {
      p_phlebotomist_id: phleb.id,
      p_new_payment_info_id: inserted.id,
    }).catch(() => {});

    await supabase
      .from('phlebotomist_profiles')
      .update({ current_payment_info_id: inserted.id, updated_at: now })
      .eq('id', phleb.id);

    setInfo(inserted);
    setSaving(false);
    toast({ title: 'Payment info updated', status: 'success', duration: 2500 });
  };

  if (loading) return <Flex justify="center" py={8}><Spinner /></Flex>;

  return (
    <VStack align="stretch" spacing={4} maxW="700px">
      <Alert status="info" fontSize="sm" borderRadius="md">
        <AlertIcon />
        ACH bank details. Re-enter the account number when you change banks — we don't show the existing one for security.
      </Alert>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <Field label="Account name" value={form.bank_account_name} onChange={(v) => setForm({ ...form, bank_account_name: v })} />
        <Field label="Bank name" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} />
        <Field label="Routing number" value={form.bank_routing_number} onChange={(v) => setForm({ ...form, bank_routing_number: v })} />
        <Field label="Account number" value={form.bank_account_number} onChange={(v) => setForm({ ...form, bank_account_number: v })} type="password" />
        <FormControl>
          <FormLabel fontSize="sm">Account type</FormLabel>
          <Select value={form.bank_account_type} onChange={(e) => setForm({ ...form, bank_account_type: e.target.value })}>
            <option value="checking">Checking</option>
            <option value="savings">Savings</option>
          </Select>
        </FormControl>
      </SimpleGrid>
      <HStack justify="space-between">
        <Text fontSize="xs" color="gray.500">
          Last updated {info?.created_at ? new Date(info.created_at).toLocaleDateString() : 'never'}
        </Text>
        <Button colorScheme="blue" onClick={save} isLoading={saving}>Update payment info</Button>
      </HStack>
    </VStack>
  );
}

// ── Credentials ─────────────────────────────────────────────────────────────
function CredentialsSection({ phleb, onSaved, toast }) {
  const [value, setValue] = useState({
    drivers_license: phleb.drivers_license_path
      ? { path: phleb.drivers_license_path, filename: phleb.drivers_license_filename }
      : null,
    phlebotomy_cert: phleb.phlebotomy_cert_path
      ? { path: phleb.phlebotomy_cert_path, filename: phleb.phlebotomy_cert_filename }
      : null,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const now = new Date().toISOString();
    const patch = {
      drivers_license_path:     value.drivers_license?.path || null,
      drivers_license_filename: value.drivers_license?.filename || null,
      drivers_license_uploaded_at: value.drivers_license ? now : null,
      phlebotomy_cert_path:     value.phlebotomy_cert?.path || null,
      phlebotomy_cert_filename: value.phlebotomy_cert?.filename || null,
      phlebotomy_cert_uploaded_at: value.phlebotomy_cert ? now : null,
    };
    const { data, error } = await supabase
      .from('phlebotomist_profiles')
      .update(patch)
      .eq('id', phleb.id)
      .select()
      .maybeSingle();
    setSaving(false);
    if (error) { toast({ title: 'Could not save', description: error.message, status: 'error' }); return; }
    if (data) onSaved(data);
    toast({ title: 'Credentials updated', status: 'success', duration: 2500 });
  };

  return (
    <VStack align="stretch" spacing={4}>
      <CredentialsUpload phlebId={phleb.id} value={value} onChange={setValue} />
      <HStack justify="flex-end">
        <Button colorScheme="blue" onClick={save} isLoading={saving}>Save credentials</Button>
      </HStack>
    </VStack>
  );
}

// ── Documents ───────────────────────────────────────────────────────────────
function DocumentsSection({ phleb }) {
  const [w9Url, setW9Url] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!phleb.w9_pdf_path) return;
    (async () => {
      const { data } = await supabase.storage.from('phleb-credentials').createSignedUrl(phleb.w9_pdf_path, 1800);
      if (!cancelled) setW9Url(data?.signedUrl || null);
    })();
    return () => { cancelled = true; };
  }, [phleb.w9_pdf_path]);

  return (
    <VStack align="stretch" spacing={3} maxW="700px">
      <DocRow
        label="Signed IRS Form W-9"
        sub={phleb.w9_signed_at ? `Signed ${new Date(phleb.w9_signed_at).toLocaleDateString()}` : 'Not signed yet'}
        url={w9Url}
      />
      <DocRow
        label="Service Agreement"
        sub={phleb.agreement_signed_at ? `Signed ${new Date(phleb.agreement_signed_at).toLocaleDateString()}` : 'Not signed yet'}
        url={null}
        hint="Available from admin on request."
      />
    </VStack>
  );
}

function DocRow({ label, sub, url, hint }) {
  return (
    <Flex bg="white" borderWidth="1px" borderRadius="md" p={3} justify="space-between" align="center">
      <Box>
        <Text fontWeight="600" fontSize="sm">{label}</Text>
        <Text fontSize="xs" color="gray.500">{sub}</Text>
      </Box>
      {url ? (
        <Link href={url} isExternal>
          <Button size="sm" variant="outline" rightIcon={<FaExternalLinkAlt />}>Open</Button>
        </Link>
      ) : (
        <Text fontSize="xs" color="gray.400">{hint || '—'}</Text>
      )}
    </Flex>
  );
}

// ── Security ────────────────────────────────────────────────────────────────
function SecuritySection({ phleb, toast, navigate }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (pw.length < 8) { toast({ title: 'Password must be at least 8 characters', status: 'warning' }); return; }
    if (pw !== pw2) { toast({ title: "Passwords don't match", status: 'warning' }); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    if (error) { toast({ title: 'Could not update password', description: error.message, status: 'error' }); setSaving(false); return; }
    await supabase.from('phlebotomist_profiles').update({ password_set_at: new Date().toISOString() }).eq('id', phleb.id);
    setSaving(false);
    setPw(''); setPw2('');
    toast({ title: 'Password updated', status: 'success', duration: 2500 });
  };

  const logout = async () => {
    await supabase.auth.signOut().catch(() => {});
    localStorage.removeItem('phlebotomistUser');
    navigate('/phlebotomist/login');
  };

  return (
    <VStack align="stretch" spacing={5} maxW="500px">
      <Box>
        <Heading size="sm" mb={2}>Change password</Heading>
        <VStack align="stretch" spacing={3}>
          <Field label="New password" type="password" value={pw} onChange={setPw} />
          <Field label="Confirm new password" type="password" value={pw2} onChange={setPw2} />
          <Button colorScheme="blue" alignSelf="flex-start" onClick={save} isLoading={saving}>Update password</Button>
        </VStack>
      </Box>
      <Divider />
      <Box>
        <Heading size="sm" mb={2}>Sign out</Heading>
        <Button leftIcon={<FaSignOutAlt />} variant="outline" colorScheme="red" onClick={logout}>
          Sign out of this device
        </Button>
      </Box>
      {phleb.password_set_at && (
        <Text fontSize="xs" color="gray.500">
          Password last set {new Date(phleb.password_set_at).toLocaleString()}
        </Text>
      )}
    </VStack>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <FormControl>
      <FormLabel fontSize="sm">{label}</FormLabel>
      <Input size="sm" type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </FormControl>
  );
}
